import apiClient from "../utils/axiosConfig";
import { getApiUrl, API_ENDPOINTS } from "../Configurations/config";

const BRAND_TIMEOUT_MS = 300000;

export async function getMonths() {
  const res = await apiClient.get(getApiUrl(API_ENDPOINTS.BRAND.MONTHS), {
    timeout: BRAND_TIMEOUT_MS,
  });
  return res.data;
}

export async function getDivisions(month) {
  const res = await apiClient.get(getApiUrl(API_ENDPOINTS.BRAND.DIVISIONS), {
    params: { month },
    timeout: BRAND_TIMEOUT_MS,
  });

  return res.data;
}

export async function getBrands(month, divisionId) {
  const res = await apiClient.get(getApiUrl(API_ENDPOINTS.BRAND.BRANDS), {
    params: {
      month,
      division: divisionId,
    },
    timeout: BRAND_TIMEOUT_MS,
  });

  return res.data;
}

export async function getTerritoryPerformance({
  month,
  divisionId,
  brandId,
  page,
  page_size,
}) {
  const [summaryRes, itemsRes] = await Promise.all([
    apiClient.get(getApiUrl(API_ENDPOINTS.BRAND.TERRITORY_SUMMARY), {
      params: {
        month,
        division: divisionId,
        brand: brandId,
      },
      timeout: BRAND_TIMEOUT_MS,
    }),
    apiClient.get(getApiUrl(API_ENDPOINTS.BRAND.TERRITORIES), {
      params: {
        month,
        division: divisionId,
        brand: brandId,
        page,
        page_size,
      },
      timeout: BRAND_TIMEOUT_MS,
    }),
  ]);

  const summary = Array.isArray(summaryRes.data)
    ? summaryRes.data[0] || {}
    : summaryRes.data?.data || summaryRes.data || {};

  const rawItems = Array.isArray(itemsRes.data)
    ? itemsRes.data
    : itemsRes.data?.data || [];

  const items = rawItems.map((item) => ({
    id: item.name,
    name: item.name,
    achievement: Number(item.achievement || 0),
    sales: Number(item.salesLakh ?? 0),
    target: Number(item.target || 0),
    salesOfficers: Number(item.salesOfficers || 0),
    stockists: Number(item.stockists || 0),
    growth: Number(item.growth || 0),
    Actions: item.Actions,
  }));

  return {
    summary,
    items,
    total: itemsRes.data?.total || 0,
  };
}

export async function getSalesOfficers({
  month,
  division,
  brand,
  territory,
  page,
  page_size,
}) {
  const res = await apiClient.get(
    getApiUrl(API_ENDPOINTS.BRAND.SALES_OFFICERS_COVERAGE),
    {
      params: {
        month,
        division,
        brand,
        territory,
        page,
        page_size,
      },
      timeout: BRAND_TIMEOUT_MS,
    }
  );

  const data = res.data || {};

  return {
    metrics: {
      totalSalesOfficers: data.kpi?.total_sales_officers || 0,
      avgDoctorsPerEmployee: data.kpi?.avg_doctors_per_employee || 0,
      avgDoctorsCoveredPerEmployee: data.kpi?.avg_covered_doctors_per_employee || 0,
      averageCoverage: data.kpi?.avg_coverage || 0,
    },

    items: (data.employee_coverage || []).map((e) => ({
      employeeCode: e.employee_code,
      employeeName: e.employee_name,
      designation: e.designation,
      totalDoctors: e.total_doctors,
      coveredDoctors: e.covered_doctors,
      coverage: e.coverage_pct,
    })),

    total: data.total || 0,
  };
}

export async function getDoctors({
  month,
  division,
  brand,
  territory,
  specialty,
  potential,
  employeeId,
  page = 1,
  page_size = 10,
  category,
}) {
  const params = {
    month,
    division,
    brand,
    territory,
    page,
    page_size,
  };

  if (category) params.category = category;
  if (specialty) params.speciality = specialty;
  if (potential) params.potential = potential;
  if (employeeId) params.employee_name = employeeId;

  const res = await apiClient.get(getApiUrl(API_ENDPOINTS.BRAND.DOCTOR_DETAILS), {
    params,
    timeout: BRAND_TIMEOUT_MS,
  });

  return res.data;
}

export async function getStockists({
  month,
  division,
  brand,
  territory,
  page,
  page_size,
  category = "ALL",
  time_filter = "MTD",
}) {
  const res = await apiClient.get(getApiUrl(API_ENDPOINTS.BRAND.STOCKIST_DETAILS), {
    params: {
      month,
      division,
      brand,
      territory,
      page,
      page_size,
      category,
      time_filter,
    },
    timeout: BRAND_TIMEOUT_MS,
  });

  const data = res.data || {};
  const summary = data.summary || {};

  return {
    summary,
    metrics: {
      totalStockists: summary.total_stockists || 0,
      totalMtdSales: summary.total_mtd_sales || 0,
      totalSales: summary.total_sales ?? summary.total_mtd_sales ?? 0,
      averageGrowth: summary.avg_growth || 0,
      positiveGrowthStockists: summary.positive_growth_stockists || 0,
      breakage: (summary.breakage || 0) * -1 || 0,
      return: (summary.sales_return || 0) * -1 || 0,
      expiry: (summary.expiry || 0) * -1 || 0,
    },

    items: (data.stockists || []).map((s, index) => ({
      id: `${s.stockist_name}-${index}`,
      stockistName: s.stockist_name,
      mtdSales: s.mtd_sales,
      lastYearMtdSales: s.last_year_mtd_sales,
      sales: s.sales ?? s.mtd_sales ?? 0,
      lastYearSales: s.last_year_sales ?? s.last_year_mtd_sales ?? 0,
      growth: s.growth_pct ?? 0,
    })),

    total: data.total || 0,
  };
}
